<template>
  <ToastProvider :duration="4000" swipe-direction="up">
    <slot />

    <ToastViewport
      class="fixed top-4 left-1/2 z-[300] flex max-h-screen w-full max-w-[min(100vw-2rem,420px)] -translate-x-1/2 flex-col gap-2 p-0 outline-none"
      :label="t('toast.viewportLabel')"
    />

    <ToastRoot
      v-for="toastItem in toastState.toasts"
      :key="toastItem.id"
      :open="toastItem.open"
      :duration="toastItem.duration ?? 4000"
      :class="toastRootClass(toastItem.variant)"
      @update:open="(open) => handleToastOpenChange(toastItem.id, open)"
    >
      <div class="flex w-full items-start gap-3">
        <component :is="iconForVariant(toastItem.variant)" class="mt-0.5 h-4 w-4 shrink-0" :class="iconClass(toastItem.variant)" />
        <div class="min-w-0 flex-1 space-y-1">
          <ToastTitle class="text-sm font-medium leading-5">
            {{ toastItem.title }}
          </ToastTitle>
          <ToastDescription
            v-if="toastItem.description"
            class="text-xs leading-5 text-muted-foreground"
          >
            {{ toastItem.description }}
          </ToastDescription>
        </div>
        <ToastClose
          class="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          :aria-label="t('common.close')"
        >
          <X class="h-4 w-4" />
        </ToastClose>
      </div>
    </ToastRoot>
  </ToastProvider>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
} from 'reka-ui';
import {
  AlertTriangle,
  CircleAlert,
  CircleCheck,
  Info,
  MessageSquare,
  X,
} from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { handleToastOpenChange, toastState, type ToastVariant } from '@/shared/feedback/toast-state';

const { t } = useI18n();

function toastRootClass(variant: ToastVariant) {
  return cn(
    'toast-item group pointer-events-auto relative flex w-full items-start overflow-hidden rounded-lg border px-4 py-3 shadow-notion-lg backdrop-blur-sm',
    'data-[swipe=move]:translate-y-[var(--reka-toast-swipe-move-y)] data-[swipe=end]:translate-y-[var(--reka-toast-swipe-end-y)] data-[swipe=end]:animate-out',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
    'data-[state=open]:slide-in-from-top-1 data-[state=closed]:slide-out-to-top-1',
    'data-[state=open]:duration-500 data-[state=closed]:duration-300',
    {
      default: 'border-border bg-background/95 text-foreground',
      success: 'border-success/30 bg-success/10 text-foreground',
      error: 'border-destructive/30 bg-destructive/10 text-foreground',
      warning: 'border-warning/30 bg-warning/10 text-foreground',
      info: 'border-info/30 bg-info/10 text-foreground',
    }[variant],
  );
}

function iconClass(variant: ToastVariant) {
  return {
    default: 'text-muted-foreground',
    success: 'text-success',
    error: 'text-destructive',
    warning: 'text-warning',
    info: 'text-info',
  }[variant];
}

function iconForVariant(variant: ToastVariant) {
  return {
    default: MessageSquare,
    success: CircleCheck,
    error: CircleAlert,
    warning: AlertTriangle,
    info: Info,
  }[variant];
}
</script>

<style scoped>
:deep(.toast-item[data-state='open']) {
  --tw-enter-opacity: 0;
  --tw-enter-translate-y: -0.375rem;
  animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
}

:deep(.toast-item[data-state='closed']) {
  --tw-exit-opacity: 0;
  --tw-exit-translate-y: -0.25rem;
  animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
